import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocationCity,
  Save,
  Close,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface City {
  _id?: string;
  plateCode: number;
  cityName: string;
  createdAt?: string;
  updatedAt?: string;
}

const CityManagement: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Form states
  const [newCity, setNewCity] = useState<City>({ plateCode: 0, cityName: '' });
  const [editCity, setEditCity] = useState<City>({ plateCode: 0, cityName: '' });
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  // Bulk add state
  const [bulkCities, setBulkCities] = useState<City[]>([{ plateCode: 0, cityName: '' }]);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getInitialConfig();
      if (response.status) {
        setCities(response.data?.cities || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şehirler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCity = async () => {
    if (!newCity.plateCode || !newCity.cityName.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminService.addCities([newCity]);
      setSuccess('Şehir başarıyla eklendi!');
      setAddDialog(false);
      setNewCity({ plateCode: 0, cityName: '' });
      await fetchCities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şehir eklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    const validCities = bulkCities.filter(c => c.plateCode > 0 && c.cityName.trim());
    
    if (validCities.length === 0) {
      setError('Lütfen en az bir geçerli şehir bilgisi girin.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminService.addCities(validCities);
      setSuccess(`${validCities.length} şehir başarıyla eklendi!`);
      setAddDialog(false);
      setBulkCities([{ plateCode: 0, cityName: '' }]);
      await fetchCities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şehirler eklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCity = async () => {
    if (!editCity.plateCode || !editCity.cityName.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminService.updateCities([editCity]);
      setSuccess('Şehir başarıyla güncellendi!');
      setEditDialog(false);
      await fetchCities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şehir güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCity = async () => {
    if (!selectedCity) return;

    setLoading(true);
    setError('');
    try {
      await adminService.deleteCity(selectedCity.plateCode);
      setSuccess('Şehir başarıyla silindi!');
      setDeleteDialog(false);
      setSelectedCity(null);
      await fetchCities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şehir silinirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const addBulkCityRow = () => {
    setBulkCities([...bulkCities, { plateCode: 0, cityName: '' }]);
  };

  const removeBulkCityRow = (index: number) => {
    setBulkCities(bulkCities.filter((_, i) => i !== index));
  };

  const updateBulkCity = (index: number, field: 'plateCode' | 'cityName', value: any) => {
    const updated = [...bulkCities];
    if (field === 'plateCode') {
      updated[index].plateCode = parseInt(value) || 0;
    } else {
      updated[index].cityName = value;
    }
    setBulkCities(updated);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Şehir Yönetimi
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sistemdeki şehirleri görüntüleyin ve yönetin.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialog(true)}
          size="large"
        >
          Şehir Ekle
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocationCity color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {cities.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Şehir
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Plaka Kodu</TableCell>
                <TableCell>Şehir Adı</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : cities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Henüz şehir eklenmemiş.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cities.map((city) => (
                  <TableRow
                    key={city.plateCode}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Chip label={city.plateCode} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {city.cityName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {city.createdAt
                        ? new Date(city.createdAt).toLocaleDateString('tr-TR')
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setEditCity(city);
                          setEditDialog(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedCity(city);
                          setDeleteDialog(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Şehir Ekle (Bulk)
            <IconButton onClick={() => setAddDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Birden fazla şehri aynı anda ekleyebilirsiniz.
          </Typography>
          {bulkCities.map((city, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Plaka Kodu"
                type="number"
                value={city.plateCode || ''}
                onChange={(e) => updateBulkCity(index, 'plateCode', e.target.value)}
                sx={{ width: 150 }}
              />
              <TextField
                label="Şehir Adı"
                value={city.cityName}
                onChange={(e) => updateBulkCity(index, 'cityName', e.target.value)}
                fullWidth
              />
              {bulkCities.length > 1 && (
                <IconButton color="error" onClick={() => removeBulkCityRow(index)}>
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          <Button startIcon={<Add />} onClick={addBulkCityRow} variant="outlined" fullWidth>
            Satır Ekle
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>İptal</Button>
          <Button onClick={handleBulkAdd} variant="contained" startIcon={<Save />} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Şehir Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            label="Plaka Kodu"
            type="number"
            value={editCity.plateCode || ''}
            onChange={(e) => setEditCity({ ...editCity, plateCode: parseInt(e.target.value) || 0 })}
            fullWidth
            sx={{ mb: 2, mt: 2 }}
            disabled
          />
          <TextField
            label="Şehir Adı"
            value={editCity.cityName}
            onChange={(e) => setEditCity({ ...editCity, cityName: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>İptal</Button>
          <Button onClick={handleUpdateCity} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Şehir Sil</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedCity?.cityName}</strong> ({selectedCity?.plateCode}) şehrini silmek
            istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>İptal</Button>
          <Button onClick={handleDeleteCity} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CityManagement;

